import { useNavigate } from 'react-router-dom'
import { Mail, Clock, CheckCircle, XCircle, Users } from 'lucide-react'
import { Card, CardContent, Button, Badge } from '@/components/ui'
import { LoadingSpinner } from '@/components/shared'
import { usePendingInvitations, getRoleDisplayName } from '@/hooks'

export function PendingInvitationsPage() {
  const navigate = useNavigate()
  const {
    invitations,
    isLoading,
    acceptInvitation,
    declineInvitation,
    isAccepting,
    isDeclining,
  } = usePendingInvitations()

  const handleAccept = async (invitationId: string) => {
    try {
      await acceptInvitation(invitationId)
      // After accepting, if this was the last/only invitation, redirect to dashboard
      if (invitations.length === 1) {
        navigate('/')
      }
    } catch (err) {
      // Error handling is done in the hook
      console.error('Failed to accept invitation:', err)
    }
  }

  const handleDecline = async (invitationId: string) => {
    try {
      await declineInvitation(invitationId)
    } catch (err) {
      console.error('Failed to decline invitation:', err)
    }
  }

  const handleCreateFamily = () => {
    navigate('/onboarding/create-family')
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-lg px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
            <Mail className="h-8 w-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">You Have Invitations!</h1>
          <p className="mt-1 text-gray-500">
            Accept an invitation to join a family or create your own
          </p>
        </div>

        {/* Invitations list */}
        <div className="space-y-4">
          {invitations.map((invitation) => {
            const expiresAt = new Date(invitation.expires_at)
            const isExpired = expiresAt < new Date()

            return (
              <Card key={invitation.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                        <Users className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          Family Invitation
                        </p>
                        <p className="text-sm text-gray-500">
                          You're invited as a{' '}
                          <span className="font-medium">
                            {getRoleDisplayName(invitation.role)}
                          </span>
                        </p>
                        <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                          <Clock className="h-3 w-3" />
                          {isExpired ? (
                            <span className="text-error-500">Expired</span>
                          ) : (
                            <span>
                              Expires {expiresAt.toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge variant={isExpired ? 'error' : 'secondary'}>
                      {isExpired ? 'Expired' : 'Pending'}
                    </Badge>
                  </div>

                  {!isExpired && (
                    <div className="mt-4 flex gap-2">
                      <Button
                        onClick={() => handleAccept(invitation.id)}
                        disabled={isAccepting || isDeclining}
                        className="flex-1"
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Accept
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDecline(invitation.id)}
                        disabled={isAccepting || isDeclining}
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Decline
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Divider */}
        <div className="my-8 flex items-center gap-4">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-sm text-gray-500">or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        {/* Create family option */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Create your own family</p>
                <p className="text-sm text-gray-500">
                  Start a new family and invite others to join
                </p>
              </div>
              <Button variant="outline" onClick={handleCreateFamily}>
                Create Family
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Skip link */}
        {invitations.length === 0 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              No pending invitations found.{' '}
              <button
                type="button"
                onClick={handleCreateFamily}
                className="font-medium text-primary-600 hover:text-primary-700"
              >
                Create a family
              </button>{' '}
              to get started.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
